import React, { type FC, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import type { EdgeProps, NodeProps } from '@xyflow/react'
import { Flow } from 'widgets/Flow'
import { SimpleFlowEdge } from 'widgets/Flow/ui/SimpleFlowEdge/SimpleFlowEdge'
import { RingNode } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring'
import { CustomEdgeWithLabel } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/CustomEdge/CustomEdgeWithLabel'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'
import { getNodesByChangesMap } from '../utils/getNodesByChangesMap'
import {
  CallEndsNode,
  CallQueueNode,
  ForwardCallNode,
  IncomingCallNode,
  PlayAudioNode,
  SendToVoicemailNode,
} from './actions'
import { CustomEdge } from './CustomEdge/CustomEdge'
import { SelectedAction } from './SelectedAction/SelectedAction'
import { useSmartCallFlowBuilderContext } from '../context'

type IExistNodes = Extract<
  ISmartCallFlowActionType,
  | 'incomingCall'
  | 'ring'
  | 'sendToVoicemail'
  | 'playAudio'
  | 'forwardCall'
  | 'callEnds'
  | 'call_queue'
>

export type ISmartCallFlowBuilderUiProps = {
  viewOnly?: boolean
}

export type ISmartCallNodeProps = NodeProps & { viewOnly?: boolean }

export const SmartCallFlowBuilderUi = observer(({ viewOnly }: ISmartCallFlowBuilderUiProps) => {
  const { actions, getActionById } = useSmartCallFlowBuilderContext()

  const edgeTypes = useMemo(
    () => ({
      simple: (props: EdgeProps) => <SimpleFlowEdge viewOnly={viewOnly} {...props} />,
      customEdge: (props: EdgeProps) => <CustomEdge viewOnly={viewOnly} {...props} />,
      customEdgeWithLabel: (props: EdgeProps) => (
        <CustomEdgeWithLabel viewOnly={viewOnly} {...props} />
      ),
    }),
    [viewOnly]
  )

  const nodeTypes: Record<IExistNodes, FC<NodeProps>> = useMemo(
    () => ({
      incomingCall: (props: NodeProps) => <IncomingCallNode viewOnly={viewOnly} {...props} />,
      sendToVoicemail: (props: NodeProps) => <SendToVoicemailNode viewOnly={viewOnly} {...props} />,
      ring: (props: NodeProps) => <RingNode viewOnly={viewOnly} {...props} />,
      playAudio: (props: NodeProps) => <PlayAudioNode viewOnly={viewOnly} {...props} />,
      forwardCall: (props: NodeProps) => <ForwardCallNode viewOnly={viewOnly} {...props} />,
      callEnds: (props: NodeProps) => <CallEndsNode viewOnly={viewOnly} {...props} />,
      call_queue: (props: NodeProps) => <CallQueueNode viewOnly={viewOnly} {...props} />,
    }),
    [viewOnly]
  )

  return (
    <Flow
      actions={actions}
      getActionById={getActionById}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      getNodesByChangesMap={getNodesByChangesMap}
      selectedAction={<SelectedAction />}
      viewOnly={viewOnly}
    />
  )
})
